import { Alert } from "../Alert";
import { AnswerText } from "./AnswerText";
import { SearchResults } from "./SearchResults";
import { TypingIndicator } from "./TypingIndicator";
import type { DomainResolution, SearchResponse } from "../../api/types";
import type { ChatTurn } from "../../hooks/useChat";

/** Human-readable labels for the tools a turn may report while it works. */
const ACTIVITY: Record<string, string> = {
  search_files: "Searching files…",
  resolve_domain: "Working out which client or project you mean…",
  list_domains: "Listing what can be searched…",
  get_index_status: "Checking how fresh the index is…",
  get_file_metadata: "Reading file details…",
  get_file_text: "Reading the file…",
  request_domain_index_refresh: "Requesting a re-index…",
};

/** One turn in the conversation: the words, then whatever the tools returned to support them. */
export function ChatTurnView({
  turn,
  resultsShown,
  onViewAll,
}: {
  turn: ChatTurn;
  resultsShown: number;
  onViewAll: (result: SearchResponse) => void;
}): JSX.Element {
  const isUser = turn.role === "user";
  return (
    <div className={`chat-turn chat-turn--${turn.role}`}>
      {turn.text && (
        <div className="chat-turn__bubble">
          <AnswerText text={turn.text} />
        </div>
      )}

      {!isUser && turn.pending && !turn.text && (
        <TypingIndicator label={ACTIVITY[turn.activity] ?? "Thinking…"} />
      )}

      {turn.domains.map((resolution, index) => (
        <p className="chat-turn__notice" key={`domain-${index}`}>
          {resolution.notice || `“${resolution.query}” matches ${scopes(resolution)}.`}
        </p>
      ))}

      {turn.searches.map((result, index) => (
        <SearchResults
          key={`${result.domain_restriction.join(",")}-${index}`}
          result={result}
          shown={resultsShown}
          onViewAll={onViewAll}
        />
      ))}

      {turn.error && <Alert variant="error">{turn.error}</Alert>}
    </div>
  );
}

function scopes(resolution: DomainResolution): string {
  const count = resolution.candidates.length;
  return `${count} ${count === 1 ? "scope" : "scopes"}`;
}
